/**
 * The stream a viewer watches (F5, D210): a floating card over the chat with
 * a canvas the voice worker's decoded VideoFrames land on. The canvas is the
 * whole render path — frames arrive via `registerSharePainter`, are drawn
 * once and closed; there is no <video> element because there is no
 * MediaStream (share video rides the DataChannel, not an m-line).
 */
import { type JSX, Show, createEffect, onCleanup, onMount } from "solid-js";

import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { Runtime } from "../store/runtime";
import type { ShareView } from "../store/types";
import { Icon } from "./ds";

export function StreamView(props: { rt: Runtime; t: Dict; share: ShareView }): JSX.Element {
  const rt = props.rt;
  const t = props.t;
  let canvas: HTMLCanvasElement | undefined;

  onMount(() => {
    const ctx = canvas?.getContext("2d");
    rt.registerSharePainter((_senderId, frame) => {
      if (canvas && ctx) {
        // Size the bitmap to the stream once (or when the sharer's source
        // changes size); CSS keeps the on-screen box.
        if (canvas.width !== frame.displayWidth || canvas.height !== frame.displayHeight) {
          canvas.width = frame.displayWidth;
          canvas.height = frame.displayHeight;
        }
        ctx.drawImage(frame, 0, 0);
      }
      frame.close();
    });
  });
  onCleanup(() => rt.registerSharePainter(null));

  // A paused source produces no frames (§8) — the last one stays on the
  // canvas and the veil says why nothing moves.
  createEffect(() => void props.share.paused);

  const sharerName = () => mut.nameOf(rt.state.directory, props.share.userId);

  return (
    <div class="web-stream-view surface-pane">
      <div class="web-stream-view__header">
        <Icon name="cast" size={14} />
        <span class="web-stream-view__title">{t.streamOf(sharerName())}</span>
        <span class="layout-spacer" />
        <Show when={props.share.hasAudio}>
          <input
            class="voice-peer__volume"
            type="range"
            min="0"
            max="200"
            value={props.share.volume * 100}
            title={t.shareVolume()}
            onInput={(e) => rt.setShareVolume(Number(e.currentTarget.value) / 100)}
          />
        </Show>
        <button
          type="button"
          class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
          title={t.stopWatching()}
          onClick={() => rt.stopWatching()}
        >
          <Icon name="close" size={15} />
        </button>
      </div>
      <div class="web-stream-view__stage">
        <canvas ref={canvas} class="web-stream-view__canvas" />
        <Show when={props.share.paused}>
          <div class="web-stream-view__veil">{t.streamPaused()}</div>
        </Show>
      </div>
    </div>
  );
}
